import { FREQUENCY } from './dropdownOptions';
import { FREQUENCY_GROUPS } from './frequencyGroups';
import { INCOME_TYPES, OTHER_INCOME_TYPES, PARAMETER_TYPES } from './incomeAndExpenceTypes';
import { FREQUENCY_TYPES } from './frequencyTypes';

const TWICE_YEARLY_MN_INCOME_CONDITIONS = [
    INCOME_TYPES.JOB,
    INCOME_TYPES.INVESTMENT,
    INCOME_TYPES.SCHOLARSHIP,
    INCOME_TYPES.FARMING_FISHING,
].map(incomeType => ({ incomeType }));

const CONDITION_MATCHERS = {
    incomeType: (expected, { incomeType }) => incomeType === expected,
    subtype: (expected, { subtype }) => subtype === expected,
    parameter: (expected, { parameter }) => parameter === expected,
};

/** Named context matchers for visibleWhen rules */
const VISIBILITY_CONTEXT = {
    INCOMES: { parameter: PARAMETER_TYPES.INCOMES },
    PAID_FAMILY_LEAVE: { subtype: OTHER_INCOME_TYPES.PAID_FAMILY_LEAVE },
    EXPENSES: { parameter: PARAMETER_TYPES.EXPENSES },
};

/**
 * Frequency dropdown configuration.
 *
 * Three layers (read in order):
 * 1. FREQUENCY_GROUPS — base whitelist per source category, shared by all states (see frequencyGroups.js)
 * 2. INCOME_SOURCES / DEDUCTIONS_SOURCES — each source points at one FREQUENCY_GROUP
 * 3. FREQUENCY_OPTIONS_CONFIG (this file) — state-specific show/hide rules on top
 *
 * `base` holds rules shared by all states. State entries (MN, NM, …) should only list
 * differences from `base` — omitted frequencies inherit base rules; listed frequencies
 * replace the base rule for that key entirely.
 *
 * State keys:
 * - options — per-frequency visibility rules
 *
 * Source-level group swaps (e.g. MN paid family leave on Other income) use
 * frequencyGroupBySubtype on INCOME_SOURCES entries — see frequencyGroups.js.
 *
 * Option rule shape:
 * - { hidden: true }
 * - { hiddenForIncomeTypes: [...] }
 * - { visible: true } — overrides a base rule (e.g. MN Yearly for self-employment)
 * - { visibleWhen: [...] } — shown when a condition matches (can apply beyond the source group)
 */
export const FREQUENCY_OPTIONS_CONFIG = {
    base: {
        options: {
            [FREQUENCY_TYPES.SEASONAL]: { hidden: true },
            [FREQUENCY_TYPES.YEARLY]: {
                hiddenForIncomeTypes: [INCOME_TYPES.SELF_EMPLOYMENT],
            },
            [FREQUENCY_TYPES.TWICE_YEARLY]: { hidden: true },
        },
    },
    MN: {
        options: {
            [FREQUENCY_TYPES.HOURLY]: { hidden: true },
            [FREQUENCY_TYPES.DAILY]: { hidden: true },
            [FREQUENCY_TYPES.QUARTERLY]: { visibleWhen: [VISIBILITY_CONTEXT.INCOMES] },
            [FREQUENCY_TYPES.YEARLY]: { visible: true },
            [FREQUENCY_TYPES.TWICE_YEARLY]: {
                visibleWhen: [
                    VISIBILITY_CONTEXT.PAID_FAMILY_LEAVE,
                    VISIBILITY_CONTEXT.EXPENSES,
                    ...TWICE_YEARLY_MN_INCOME_CONDITIONS,
                ],
            },
        },
    },
    NM: {
        options: {
            [FREQUENCY_TYPES.SEASONAL]: { visible: true },
        },
    },
};

/** Merges `base` with the active state entry. State `options` replace base rules per frequency key; arrays are concatenated. */
const mergeConfig = isState => {
    const { base } = FREQUENCY_OPTIONS_CONFIG;
    const stateKey = Object.keys(FREQUENCY_OPTIONS_CONFIG).find(key => key !== 'base' && isState[key]);
    const stateConfig = stateKey ? FREQUENCY_OPTIONS_CONFIG[stateKey] : {};

    return {
        options: {
            ...base.options,
            ...stateConfig.options,
        },
    };
};

/** Returns true when every field on the condition matches the current modal context. Only defined fields are checked. */
const matchesCondition = (condition, context) =>
    Object.entries(condition).every(([key, value]) => CONDITION_MATCHERS[key](value, context));

/** Builds the whitelist of frequencies from the source's FREQUENCY_GROUP. */
const buildAllowedSet = frequencyGroup => new Set(FREQUENCY_GROUPS[frequencyGroup] || []);

/** Applies per-frequency option rules from config. No rule means the option is shown when present in the allowed set. */
const isOptionVisible = (rule, context) => {
    if (!rule) {
        return true;
    }

    if (rule.visibleWhen) {
        return rule.visibleWhen.some(when => matchesCondition(when, context));
    }

    if (rule.hidden) {
        return false;
    }

    if (rule.visible) {
        return true;
    }

    return !rule.hiddenForIncomeTypes?.includes(context.incomeType);
};

/** Returns dropdown options for the income/deduction modal, filtered by source group and state rules. */
export const getFrequencyOptions = (isState, incomeType, frequencyGroup, subtype = null, parameter) => {
    const config = mergeConfig(isState);
    const context = {
        incomeType,
        subtype,
        parameter: parameter ?? PARAMETER_TYPES.INCOMES,
    };
    const allowed = buildAllowedSet(frequencyGroup);

    return FREQUENCY.filter(({ value }) => {
        const rule = config.options[value];
        const inAllowedSet = allowed.has(value);

        if (!inAllowedSet && !rule?.visibleWhen) {
            return false;
        }

        return isOptionVisible(rule, context);
    });
};
